import React, { useContext, useState } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import '../../styles/components/AQILearningModal.css';
import { DevicesContext } from '../../contexts/DevicesContext';

interface AQILearningModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// AQI Parameters data structured by category (rows) and parameters (columns)

// Non-AQI Parameters

// Environmental Indicators - Different layout with 3 categories
const environmentalParameters = [
  { 
    name: 'Temperature',
    good: '0-50',
    moderate: '51-100',
    unhealthy: '101-200'
  },
  { 
    name: 'Humidity',
    good: '0-50',
    moderate: '51-100',
    unhealthy: '101-200'
  }
];

const environmentalCategories = [
  { label: 'Good', color: '#A0ED9F' },
  { label: 'Moderate', color: '#FFE48A' },
  { label: 'Unhealthy', color: '#FF8880' }
];

// Health and Risk Indicators


const AQILearningModal: React.FC<AQILearningModalProps> = ({ isOpen, onClose }) => {
  const [expandedSection, setExpandedSection] = useState<string>('aqi');
  const aqiStandardId = JSON.parse(localStorage.getItem('userPreferences') || '{}').aqiStandardId;
  const aqiStandardName =
    aqiStandardId == 1
      ? 'USA'
      : aqiStandardId == 2
        ? 'UAE'
        : aqiStandardId == 3
          ? 'INDIA'
          : '';

  // To get the AQI Standard data
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('AQILearningModal must be used within a DevicesContext');
  }
  const { aqiInfoData } = devicesContext;

  const handleClose = () => {
    onClose();
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? '' : section);
  };

  if (!isOpen) return null;

  if (aqiInfoData == null) return null
  // {
  //   return (
  //     <div className="aqi-learning-modal-overlay" onClick={handleClose}>
  //       <div className="aqi-learning-modal-new animate-scale-in zoomOutLevel" onClick={(e) => e.stopPropagation()}>
  //         <div className="h-full w-full loading-container">
  //           <div className="h-8 w-8 border-b-2 border-[var(--gradient-button-left-color)] mb-4 animate-spin rounded-full"></div>
  //           <p className="text-base text-[var(--color-sub-title-text)]">
  //             Loading AQI info data...
  //           </p>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="aqi-learning-modal-overlay" onClick={handleClose}>
      <div className="aqi-learning-modal-new animate-scale-in zoomOutLevel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="aqi-modal-header-new">
          {window.innerWidth <= 768 && <button onClick={handleClose}>
            <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                className="back-icon"
              >
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
          </button>}
          <h3 className="aqi-modal-title-new">Parameter Classification and Range Details - {aqiStandardName}</h3>
          {window.innerWidth > 768 && <button onClick={handleClose} className="aqi-modal-close-btn">
            <X size={24} />
          </button>}
        </div>

        {/* Content */}
        <div className="aqi-modal-content-new">
          {/* AQI Parameters Section */}
          <div className="aqi-accordion-section">
            <button 
              className="aqi-accordion-header"
              onClick={() => toggleSection('aqi')}
            >
              <div className="aqi-accordion-header-content">
                <img src={`${process.env.PUBLIC_URL}/AQILearningModelIcons/AQIParameters.svg`} alt="AQI Parameters" />
                <span>AQI Parameters</span>
              </div>
              {expandedSection === 'aqi' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            
            {expandedSection === 'aqi' && (
              <div className="aqi-accordion-content">
                {/* Table Header */}
                <div className="aqi-table-header">
                  <div></div>
                  {aqiInfoData.aqiParameters.map((param) => (
                    <div key={param.driverID} className="aqi-table-header-cell">
                      <div className="aqi-param-name">{param.parameterName}</div>
                      {param.unit && <div className="aqi-param-unit">({param.unit})</div>}
                    </div>
                  ))}
                </div>

                {/* Table Body */}
                <div className="aqi-table-body">
                  {aqiInfoData.aqiCategoryData.map((category, idx) => (
                    <div key={idx} className="aqi-table-row">
                      <div className="aqi-category-cell">
                        <div className="aqi-category-info">
                          <div className="aqi-category-badge" style={{ backgroundColor: category.color }}>
                          {category.category}
                          </div>
                          <div className="aqi-category-label">{category.description}</div>
                        </div>
                      </div>
                       <div className="aqi-values-grid">
                         {aqiInfoData.aqiParameters.map((param) => (
                           <div className="aqi-value-cell" data-param={param.parameterName} key={param.parameterName}>
                             <span className="param-label">{param.parameterName}{param.unit && ` (${param.unit})`}</span>
                             <span className="param-value">{category.values[param.parameterName as keyof typeof category.values]}</span>
                           </div>
                         ))}
                       </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Environmental Indicators Section */}
          <div className="aqi-accordion-section">
            <button 
              className="aqi-accordion-header"
              onClick={() => toggleSection('environmental')}
            >
              <div className="aqi-accordion-header-content">
                <img src={`${process.env.PUBLIC_URL}/AQILearningModelIcons/Environment.svg`} alt="Environmental Indicators" />
                <span>Environmental Indicators</span>
              </div>
              {expandedSection === 'environmental' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            
            {expandedSection === 'environmental' && (
              <div className="aqi-accordion-content">
                {/* Environmental Table - Simpler horizontal layout */}
                <div className="environmental-table">
                  {/* Header with categories */}
                  <div className="environmental-header">
                    <div></div>
                    {environmentalCategories.map((category, idx) => (
                      <div 
                        key={idx} 
                        className="environmental-category-header"
                        style={{ backgroundColor: category.color }}
                      >
                        {category.label}
                      </div>
                    ))}
                  </div>

                  {/* Body with parameters */}
                  <div className="environmental-body">
                    {environmentalParameters.map((param, idx) => (
                      <div key={idx} className="environmental-row">
                        <div className="environmental-param-name">{param.name}</div>
                        <div className="environmental-value">{param.good}</div>
                        <div className="environmental-value">{param.moderate}</div>
                        <div className="environmental-value">{param.unhealthy}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Non AQI Parameters Section */}
          <div className="aqi-accordion-section">
            <button 
              className="aqi-accordion-header"
              onClick={() => toggleSection('nonaqi')}
            >
              <div className="aqi-accordion-header-content">
                <img src={`${process.env.PUBLIC_URL}/AQILearningModelIcons/NonAQIParameters.svg`} alt="Non AQI Parameters" />
                <span>Non AQI Parameters</span>
              </div>
              {expandedSection === 'nonaqi' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            
            {expandedSection === 'nonaqi' && (
              <div className="aqi-accordion-content">
                {/* Table Header */}
                <div className="aqi-table-header">
                  <div></div>
                  {aqiInfoData.nonAqiParameters.map((param, idx) => (
                    <div key={idx} className="aqi-table-header-cell">
                      <div className="aqi-param-name">{param.parameterName}</div>
                      {param.unit && <div className="aqi-param-unit">({param.unit})</div>}
                    </div>
                  ))}
                </div>

                {/* Table Body */}
                <div className="aqi-table-body">
                  {aqiInfoData.nonAqiCategoryData.map((category, idx) => (
                    <div key={idx} className="aqi-table-row">
                      <div className="aqi-category-cell">
                        <div className="aqi-category-info">
                          <div className="aqi-category-badge" style={{ backgroundColor: category.color }}>
                          {category.category}
                          </div>
                          <div className="aqi-category-label">{category.description}</div>
                        </div>
                      </div>
                       <div className="aqi-values-grid">
                         {aqiInfoData.nonAqiParameters.map((param) => (
                           <div className="aqi-value-cell" data-param={param.parameterName} key={param.parameterName}>
                             <span className="param-label">{param.parameterName}{param.unit && ` (${param.unit})`}</span>
                             <span className="param-value">{category.values[param.parameterName as keyof typeof category.values]}</span>
                           </div>
                         ))}
                       </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Health and Risk Indicators Section */}
          {aqiInfoData.healthParameters.length > 0 && <div className="aqi-accordion-section">
            <button 
              className="aqi-accordion-header"
              onClick={() => toggleSection('health')}
            >
              <div className="aqi-accordion-header-content">
                <img src={`${process.env.PUBLIC_URL}/AQILearningModelIcons/Health&Risk.svg`} alt="Health and Risk Indicators" />
                <span>Health and Risk Indicators</span>
              </div>
              {expandedSection === 'health' ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            
            {expandedSection === 'health' && (
              <div className="aqi-accordion-content">
                {/* Table Header */}
                <div className="aqi-table-header">
                  <div></div>
                  {aqiInfoData.healthParameters.map((param, idx) => (
                    <div key={idx} className="aqi-table-header-cell">
                      <div className="aqi-param-name">{param.parameterName}</div>
                      {param.unit && <div className="aqi-param-unit">({param.unit})</div>}
                    </div>
                  ))}
                </div>

                {/* Table Body */}
                <div className="aqi-table-body">
                  {aqiInfoData.healthCategoryData.map((category, idx) => (
                    <div key={idx} className="aqi-table-row">
                      <div className="aqi-category-cell">
                        <div className="aqi-category-info">
                          <div className="aqi-category-badge" style={{ backgroundColor: category.color }}>
                          {category.category}
                          </div>
                          <div className="aqi-category-label">{category.description}</div>
                        </div>
                      </div>
                       <div className="aqi-values-grid">
                         {aqiInfoData.healthParameters.map((param) => (
                           <div className="aqi-value-cell" data-param={param.parameterName} key={param.parameterName}>
                             <span className="param-label">{param.parameterName}{param.unit && ` (${param.unit})`}</span>
                             <span className="param-value">{category.values[param.parameterName as keyof typeof category.values]}</span>
                           </div>
                         ))}
                       </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>}
        </div>
      </div>
    </div>
  );
};

export default AQILearningModal;
